import React, { createContext, useRef, useContext, useEffect } from 'react';
import { useHistory } from 'react-router';
import NgInReactService, {
    AngularMessage,
    OpenGlobalModalMessage
} from '~/services/ng-in-react.service';
import { isReactFirst } from '~/utils/environment.utils';
import { useModal, TModalSignature } from '~/providers/modal.provider';

const NgInReactServiceContext = createContext<NgInReactService | null>(null);

const NgModalHandler: React.FC = () => {
    const { openGlobalModal, closeGlobalModal } = useModal();
    const ngInReactService = useNgInReactService();

    useEffect(() => {
        const handleOpen = (payload: OpenGlobalModalMessage['payload']) => {
            const { componentName, ...rest } = payload;
            openGlobalModal(componentName as keyof TModalSignature, {
                data: rest,
                onClose: (data = {}) => {
                    ngInReactService.sendMessage('GlobalModalResolved', data);
                },
                onDismiss: data => {
                    ngInReactService.sendMessage('GlobalModalRejected', {});
                    closeGlobalModal(
                        componentName as keyof TModalSignature,
                        data
                    );
                },
                resolve: (data = {}) => {
                    ngInReactService.sendMessage('GlobalModalResolved', data);
                },
                reject: () => {
                    ngInReactService.sendMessage('GlobalModalRejected', {});
                }
            });
        };

        ngInReactService.ngInReactEventEmitter.on(
            'onOpenGlobalModal',
            handleOpen
        );

        return () => {
            ngInReactService.ngInReactEventEmitter.off(
                'onOpenGlobalModal',
                handleOpen
            );
        };
    }, [ngInReactService, openGlobalModal, closeGlobalModal]);

    return null;
};

const useAngularMessagesHandler = (ngInReactService: NgInReactService) => {
    const history = useHistory();
    const historyLog = useRef<Record<number, { source: 'angular' | 'react' }>>(
        {}
    );

    useEffect(() => {
        const ctrl = new AbortController();
        window.addEventListener(
            'message',
            e => {
                ngInReactService.processMessage({
                    message: e.data as AngularMessage,
                    history,
                    historyLog: historyLog.current
                });
            },
            ctrl
        );

        return () => ctrl.abort();
    }, [history, ngInReactService]);
};

const NgInReactServiceProvider: React.FC<{
    service?: NgInReactService; // Don't pass, for testing purposes only
}> = ({ children, service }) => {
    const ngInReactServiceRef = useRef<NgInReactService>(
        service ?? new NgInReactService()
    );
    const ngInReactService = ngInReactServiceRef.current;

    useAngularMessagesHandler(ngInReactService);

    return (
        <NgInReactServiceContext.Provider value={ngInReactService}>
            {children}
            <NgModalHandler />
        </NgInReactServiceContext.Provider>
    );
};

const useNgInReactService = (): NgInReactService => {
    const ngInReactService = useContext(NgInReactServiceContext);

    if (!ngInReactService && isReactFirst()) {
        throw new Error(
            'useNgIn-ReactService must be used within a NgInReactServiceProvider'
        );
    }

    return ngInReactService!;
};

export { NgInReactServiceProvider, useNgInReactService };
